import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import Footer from "@/components/layout/footer";
import Header from "@/components/layout/header";
import { ThemeProvider } from "@/components/theme/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import globalsCss from "@/styles/globals.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "pdfisfun — every PDF tool, in your browser" },
      {
        name: "description",
        content:
          "Merge, split, organize, watermark, and convert PDFs without uploading them anywhere.",
      },
      {
        name: "keywords",
        content: "pdf, merge, split, chat, organize, online, free",
      },
      { property: "og:site_name", content: "pdfisfun" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;1,9..144,400;1,9..144,500&family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap",
      },
      { rel: "stylesheet", href: globalsCss },
    ],
  }),
  component: RootDocument,
  notFoundComponent: NotFound,
});

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-32 text-center">
      <p className="text-sm text-muted-foreground">Page not found</p>
      <Link to="/" className="text-sm underline underline-offset-4">
        Go home
      </Link>
    </div>
  );
}

const setLightThemeScript = "document.documentElement.classList.add('light');";

// esbuild (used by wrangler to bundle the CF Worker) wraps named functions with
// __name() for Function.prototype.name tracking. TanStack Start serializes parts
// of the router state as inline <script> tags that end up calling __name() in the
// browser — where it isn't defined. This polyfill must run before any other script.
const esbuildNamePolyfill =
  "var __name=(fn,name)=>(Object.defineProperty(fn,'name',{value:name,configurable:true}),fn);";

function RootDocument() {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: esbuildNamePolyfill }} />
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: setLightThemeScript }} />
      </head>
      <body className="bg-background" suppressHydrationWarning>
        <ThemeProvider>
          <TooltipProvider delayDuration={150}>
            <div id="root" className="flex min-h-screen flex-col">
              <Header />
              <main className="flex-1">
                <Outlet />
              </main>
              <Footer />
              <Toaster />
            </div>
          </TooltipProvider>
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  );
}
